import { useEffect, useState, type ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { z } from 'zod';
import { DatePicker } from '../../components/ui/date-picker';
import { Skeleton } from '../../components/ui/states';
import { adminHomeDefault } from '../../lib/admin-home';
import { canUseDashboard } from '../../lib/org-role';
import { cn } from '../../lib/utils';
import { displayDate } from '../../lib/date';
import { todayIso } from '../../lib/overdue';
import { orgTimezone } from '../../lib/org-timezone';
import { useAuth } from '../../features/auth/auth-context';
import { dashboardApi, dashboardKeys } from '../../features/dashboard/api';
import { seesPeopleData } from '../../features/performance/visibility';
import { AllocationTable } from '../../features/dashboard/components/allocation-table';
import { TeamDailyTable } from '../../features/dashboard/components/team-daily-table';
import { CapacityForecastView } from '../../features/dashboard/components/capacity-forecast';
import {
  AttentionStrip,
  overviewSectionId,
} from '../../features/dashboard/components/attention-strip';
import { PortfolioTable } from '../../features/dashboard/components/portfolio-table';
import { type PortfolioFilter } from '../../features/dashboard/portfolio-model';
import { StorageByProject } from '../../features/dashboard/components/storage-by-project';
import { ApprovalQueue } from '../../features/projects/components/approval-queue';
import { MyInvitations } from '../../features/projects/components/my-invitations';
import { MyOffboardingHandovers } from '../../features/offboarding/components/my-handovers';

/**
 * Tổng quan — APM-804, spec 16.5; rút về **một tầng** ở APM-ORG-h10.
 *
 * ## Vì sao không còn ba tab
 *
 * Bản trước có ba tab theo phạm vi giảm dần: toàn tổ chức → dự án → của tôi.
 * Hai tab sau đã bỏ, vì cả hai **lặp lại một màn hình khác** mà người dùng đã
 * có sẵn trên thanh điều hướng:
 *
 * - "Của tôi" là một bản rút gọn của trang **Việc của tôi**, ngay cạnh nó trên
 *   thanh điều hướng. Hai chỗ trả lời cùng một câu, và bản rút gọn luôn là bản
 *   thiếu — không lọc được, không đổi được cách sắp xếp.
 * - "Dự án" bắt chọn một dự án từ một ô `<select>` rồi hiện hai biểu đồ của dự
 *   án ấy — trong khi bấm vào chính dự án đó ở trang **Dự án** cho cả bảy tab.
 *   Thẻ sức khoẻ đã có ở tab Báo cáo của dự án; biểu đồ mức độ cập nhật chuyển
 *   sang tab Nhân sự (APM-ORG-h9), cạnh những bảng cũng nói về con người.
 *
 * Còn lại đúng thứ **chỉ ở đây mới có**: toàn cảnh danh mục dự án, dự báo năng
 * lực, và hai hàng đợi đang chặn người khác.
 *
 * ## ⚠️ Trang này dành cho người có trách nhiệm quản lý — kể cả PM
 *
 * `canUseDashboard`: vai quản lý (`MANAGING_ORG_ROLES`) **hoặc** PM của ít nhất
 * một dự án (APM-DASH-a). PM là vai **trong dự án**, nên một nhân viên dẫn dự án
 * cũng vào được — trước đây họ bị đưa sang "Việc của tôi" và không có chỗ nào
 * xem sức khoẻ dự án mình dẫn. Nhân viên không dẫn dự án nào thì vẫn đi thẳng
 * sang "Việc của tôi": trang này với họ gần như trống.
 *
 * ⚠️ Đây là **tiện dụng**, không phải bảo mật: chuyển hướng ở client thì ai
 * cũng bỏ qua được. Chỗ chặn thật vẫn là quyền ở API.
 *
 * ## Mỗi vai thấy gì
 *
 * - **PM** (của bất kỳ dự án nào): sức khoẻ từng dự án mình dẫn, và mục tiêu
 *   S.M.A.R.T của các dự án ấy gom vào một bảng.
 * - **Trưởng nhóm có người dưới quyền**: phân bổ và hiệu suất của đội mình.
 * - **Lãnh đạo** (`report:read_portfolio`): toàn cảnh danh mục dự án, roadmap,
 *   dự báo năng lực — cộng mọi thứ trên nếu họ cũng dẫn dự án.

 * ## `asOf` chỉ đứng cạnh chart thật sự đọc nó
 *
 * `PortfolioTable` (sức khoẻ + roadmap) fold lại `event_log` tới `asOf`
 * (portfolio-query.service.ts), nhưng `CapacityForecastView`/`AllocationTable`
 * luôn nhìn kế hoạch hiện tại — đổi `asOf` không có tác dụng với hai bảng đó,
 * có chủ đích (docs/02-sprint-plan.md). Từng đặt một ô `asOf` chung ở đầu
 * trang, khiến người xem tưởng cả bốn bảng cùng đổi theo ngày chọn mà thực ra
 * chỉ hai bảng đầu đổi — nay ô `asOf` đứng ngay cạnh nhóm chart nó ảnh hưởng,
 * và có ghi chú riêng cạnh nhóm luôn-hiện-tại.
 *
 * ## Chỉ dự án đang có trên sidebar — APM-OVW-a
 *
 * Mọi mục vẽ theo dự án (sức khoẻ, mục tiêu, bảng tình trạng, roadmap) tự lọc
 * bằng `useSidebarProjectIds()` — cùng luật với thanh bên, không phải bản sao —
 * và xếp **theo đúng thứ tự sidebar** (`inSidebarOrder`).
 * Ẩn một dự án khỏi sidebar là nói "tôi không theo dõi nó". Hai bảng theo
 * **người** (năng lực, phân bổ) thì giữ nguyên: tải của một người không đổi vì
 * người xem ẩn một dự án. ⚠️ Tập ấy là tuỳ chọn **hiện tại** của người xem,
 * không phải tại `asOf`.
 *
 * ## Bảng hiệu suất nhân sự
 *
 * Người xem nó là Director (phạm vi tổ chức) **và** quản lý trực tiếp cấp
 * `LEADER`, nên cửa của nó là `seesPeopleData`, không phải
 * `report:read_portfolio`: gắn vào quyền portfolio thì nửa số người có quyền
 * xem không bao giờ thấy nó.
 *
 * Nó cũng **không** đọc `asOf`: `from`/`to` của chính nó là bộ điều khiển thời
 * gian, và hai trục thời gian trên một bảng thì không ai giải thích được cái
 * nào thắng (xem `TeamPerformancePanel`).
 *
 * Mặc định **mở** — theo yêu cầu (APM-DASH-a). Cái giá phải nói ra: mở là
 * mount, mount là gọi API, và gọi API ghi một dòng `sensitive_access_log` cho
 * **từng người** trong bảng — nên mỗi lần mở trang chủ là một lượt dấu vết, và
 * người bị ghi đọc được nó ở trang "Quyền riêng tư". Bảng vẫn đóng lại được, và
 * đóng thì không mount — không request, không dòng audit nào.
 */
export const DashboardSearch = z.object({
  /** ISO 8601. Health board và roadmap đọc lại được quá khứ. */
  asOf: z.string().optional(),
});

export const Route = createFileRoute('/_app/')({
  validateSearch: DashboardSearch,
  component: DashboardPage,
});

function DashboardPage() {
  const { t, i18n } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const goTo = useNavigate();
  const { user } = useAuth();

  const abilities = useQuery({
    queryKey: dashboardKeys.abilities,
    queryFn: dashboardApi.abilities,
  });
  // Bộ lọc mà hàng "Cần bạn xử lý" áp lên bảng danh mục — APM-OVW-d.
  const [portfolioFilter, setPortfolioFilter] = useState<PortfolioFilter | null>(null);

  /*
    Nhân viên vào đây thì đi tiếp sang "Việc của tôi" — APM-ORG-h10.

    `useEffect` chứ không `beforeLoad`: phiên được khôi phục bất đồng bộ, nên ở
    `beforeLoad` thì `user` còn là `null` và mọi người đều bị đá đi (cùng lý do
    đã ghi ở `_app.tsx`). `replace` để nút Back không ném họ ngược về đây.
  */
  useEffect(() => {
    if (user === null || !abilities.isSuccess) return;

    /*
      `SUPER_ADMIN` ở chế độ quản trị vào thẳng màn hệ thống — APM-SA-b.

      Trang này là bảng của người **điều hành dự án**: danh mục, sức khoẻ, hàng
      đợi duyệt. Người vận hành hệ thống không có việc gì ở đó, và một trang
      trống là cách chắc chắn nhất để họ thôi mở trang chủ.

      Công tắc "xem như người dùng thường" tắt đường đá này — xem `useAdminHome`.
    */
    if (adminHomeDefault(user.orgRole)) {
      void goTo({ to: '/system', replace: true });
      return;
    }

    // Chờ `abilities`: PM mang vai nhân viên chỉ nhận ra được sau khi biết họ
    // dẫn dự án nào. Quyết sớm theo mỗi vai tổ chức là đá nhầm họ đi.
    if (!canUseDashboard(user.orgRole, abilities.data, user.isOrgAdmin)) {
      void goTo({ to: '/my-tasks', replace: true });
    }
  }, [user, abilities.isSuccess, abilities.data, goTo]);

  if (abilities.isPending) return <Skeleton className="h-64 w-full" />;

  const global = abilities.data?.global ?? [];
  const canPortfolio = global.includes('report:read_portfolio');
  const canSeePeopleData = seesPeopleData(abilities.data);
  // Dự án mình **dẫn** — có `project:update`, cùng cửa với guard của API.
  const ledProjectIds = (abilities.data?.perProject ?? [])
    .filter((entry) => entry.permissions.includes('project:update'))
    .map((entry) => entry.projectId);
  // `project:update` ở **cấp tổ chức** — chỉ quản trị tổ chức có. Họ quản được
  // mọi dự án mà không cần là PM của dự án nào, nên tầng PM hiện mọi dự án trên
  // sidebar thay vì những dự án họ tình cờ dẫn (người dùng yêu cầu 2026-09-25).
  const managesAllProjects = global.includes('project:update');

  const setAsOf = (asOf: string | undefined) => void navigate({ search: { ...search, asOf } });

  const showProjects = canPortfolio || managesAllProjects || ledProjectIds.length > 0;
  const showTeam = canPortfolio || abilities.data?.hasDirectReports === true;
  const projectScope = canPortfolio || managesAllProjects ? 'sidebar' : ledProjectIds;

  return (
    // `overview-glass`: mọi khung biểu đồ trên trang mặc áo kính (index.css) —
    // APM-OVW-e, người dùng duyệt từ bản demo 2026-09-30.
    <div className="overview-glass flex flex-col gap-4">
      {/*
        Đầu trang: tên, hôm nay theo múi giờ tổ chức, và ô "xem theo ngày" cho
        lãnh đạo — ô ấy tua bảng danh mục, hàng "Cần bạn xử lý" và dung lượng
        file; năng lực và phân bổ luôn là hiện tại (ghi chú cạnh chúng).
      */}
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('nav.dashboard')}</h1>
          <p className="text-xs text-muted-foreground">{todayLabel(i18n.language)}</p>
        </div>
        {canPortfolio && (
          <AsOfField
            className="glass-bar ml-auto rounded-full py-1 pl-3 pr-1"
            value={search.asOf}
            onChange={setAsOf}
          />
        )}
      </header>

      {/*
        Hàng đợi duyệt đứng **trên** mọi thứ khác — APM-ROLE-m2a.

        Nó là việc đang chặn người khác: một dự án chờ duyệt thì cả đội của
        người tạo không bắt đầu được. Đặt nó dưới các tab thì người duyệt phải
        nhớ đi tìm, và thứ phải nhớ đi tìm là thứ bị quên. Khối này tự ẩn khi
        hàng đợi rỗng, nên nó không chiếm chỗ của phần lớn người dùng.
      */}
      {/*
        Cần bạn xử lý — APM-OVW-d: mỗi ô một con số cần hành động, đếm từ chính
        các khối bên dưới. Bấm để lọc bảng danh mục hoặc cuộn tới khối liên quan.
      */}
      <AttentionStrip
        portfolio={canPortfolio}
        scope={projectScope}
        asOf={canPortfolio ? search.asOf : undefined}
        showProjects={showProjects}
        showTeam={showTeam}
        showCapacity={canPortfolio}
        showAllocation={canSeePeopleData}
        filter={portfolioFilter}
        onFilter={setPortfolioFilter}
      />

      <div id={overviewSectionId('approvals')} className="scroll-mt-16">
        <ApprovalQueue />
      </div>

      {/* Lời mời chặn hai người: người mời chờ câu trả lời để lập kế hoạch,
          người được mời chưa biết mình có việc mới hay không. Cũng tự ẩn khi
          rỗng (APM-ROLE-o). */}
      <MyInvitations />

      {/* Cần bàn giao — APM-OFFB-f: người nghỉ việc mà mình có phần tiếp quản chưa
          lên phương án. Tự ẩn khi rỗng. */}
      <MyOffboardingHandovers />

      {/* Tầng PM — APM-DASH-a: dự án mình dẫn trước, vì đó là thứ mình chịu
          trách nhiệm trực tiếp; toàn cảnh tổ chức ở dưới. */}
      {/*
        Danh mục dự án — APM-OVW-c: **một** bảng thay cho thẻ sức khoẻ, bảng
        tình trạng, roadmap và mục tiêu S.M.A.R.T (bốn khối cùng nói về một
        nhóm dự án). Lãnh đạo đọc API danh mục và tua được về ngày cũ; PM đọc
        sức khoẻ từng dự án mình dẫn.
      */}
      {showProjects && (
        <div id={overviewSectionId('portfolio')} className="scroll-mt-16">
          <PortfolioTable
            portfolio={canPortfolio}
            scope={projectScope}
            asOf={canPortfolio ? search.asOf : undefined}
            filter={portfolioFilter}
            onClearFilter={() => setPortfolioFilter(null)}
          />
        </div>
      )}

      {/*
        Hoạt động trong ngày của đội — APM-DAY-c. Director thấy cả tổ chức,
        trưởng nhóm thấy cấp dưới trực tiếp (API quyết). Đứng trước khối danh
        mục: "hôm nay mọi người đang làm gì" là câu hỏi của buổi sáng, còn sức
        khoẻ danh mục thì đọc theo tuần. Chỉ mount khi có người để xem — mount
        cho nhân viên thường là một lượt đọc trả bảng rỗng mỗi lần mở trang.
      */}
      {showTeam && (
        <div id={overviewSectionId('team')} className="scroll-mt-16">
          <TeamDailyTable />
        </div>
      )}

      {canPortfolio && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">{t('dashboard.forwardLookingNote')}</p>
            <div className="grid gap-4 xl:grid-cols-2">
              <div id={overviewSectionId('capacity')} className="scroll-mt-16">
                <CapacityForecastView />
              </div>
              {/*
                Bảng phân bổ chỉ mount khi người dùng thật sự có quyền: mount rồi
                để API trả 403 nghĩa là mỗi lượt mở dashboard sinh một lỗi trong
                log, và một log đầy 403 hợp lệ thì không ai còn đọc log nữa.
              */}
              {canSeePeopleData && (
                <div id={overviewSectionId('allocation')} className="scroll-mt-16">
                  <AllocationTable preview />
                </div>
              )}
            </div>
          </div>

          {/*
            Dung lượng file theo dự án — APM-FILES-c. Cùng `asOf` với bảng danh
            mục. Thu về **một dòng** (APM-OVW-e), bấm để mở bảng theo dự án.
          */}
          <StorageByProject asOf={search.asOf} collapsible />
        </div>
      )}

      {/*
        Phân bổ nhân sự cho trưởng nhóm — APM-DASH-a.

        Bảng này từng chỉ nằm trong khối danh mục ở trên, tức chỉ lãnh đạo thấy.
        Nhưng API đã trả đúng cấp dưới cho quản lý trực tiếp (`seesPeopleData`),
        và "người của tôi đang dồn vào đâu" là câu trưởng nhóm hỏi trước tiên.
        Lãnh đạo đã thấy nó trong khối danh mục, nên không vẽ lần thứ hai.
      */}
      {!canPortfolio && canSeePeopleData && (
        <div id={overviewSectionId('allocation')} className="scroll-mt-16">
          <AllocationTable preview />
        </div>
      )}
    </div>
  );
}

/** "Thứ Tư, 30/09/2026" — hôm nay theo **múi giờ tổ chức**, không theo máy người xem. */
function todayLabel(language: string): string {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat(language, {
    weekday: 'long',
    timeZone: orgTimezone(),
  }).format(now);
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${displayDate(todayIso(now))}`;
}

/** Ô "xem theo ngày" — chỉ đặt cạnh chart thật sự fold lại `event_log` theo `asOf`. */
function AsOfField({
  value,
  onChange,
  className,
}: {
  value: string | undefined;
  onChange: (asOf: string | undefined) => void;
  className?: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <label className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}>
      {t('reports.asOfLabel')}
      <span
        className="inline-flex shrink-0"
        title={t('reports.asOfHint')}
        aria-label={t('reports.asOfHint')}
      >
        <Info className="size-3.5 text-muted-foreground" aria-hidden />
      </span>
      <DatePicker
        className="w-40"
        aria-label={t('reports.asOfLabel')}
        value={value?.slice(0, 10) ?? ''}
        onChange={(next) => onChange(next ? `${next}T23:59:59.999Z` : undefined)}
      />
    </label>
  );
}
